import React, { useEffect, useState, useRef } from 'react';
import { getLeadership } from '../services/api';
import './About.css';

const BELIEFS = [
  { title: 'La Bible', text: 'Nous croyons que la Bible est la Parole inspirée de Dieu, notre autorité suprême en matière de foi et de pratique.' },
  { title: 'La Trinité', text: 'Nous croyons en un seul Dieu éternel qui existe en trois personnes coégales : le Père, le Fils et le Saint-Esprit.' },
  { title: 'Le Salut', text: 'Nous croyons que le salut est un don gratuit de Dieu, reçu par la foi en Jésus-Christ seul.' },
  { title: 'La Résurrection', text: 'Nous croyons en la résurrection corporelle de Jésus-Christ et au retour glorieux du Seigneur.' },
  { title: 'L\'Église', text: 'Nous croyons que l\'Église est le corps de Christ, appelée à adorer, enseigner, servir et témoigner.' },
  { title: 'Le Service', text: 'Nous croyons que chaque croyant est appelé à servir les autres et à prendre soin des plus vulnérables.' },
];

export default function About() {
  const [members, setMembers] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    getLeadership().then(setMembers).catch(() => {});
  }, []);

  const scrollToCard = (index) => {
    const track = trackRef.current;
    if (!track || !track.children[index]) return;
    const cardWidth = track.children[0].offsetWidth;
    track.scrollTo({ left: index * (cardWidth + 24), behavior: 'smooth' });
    setActiveIdx(index);
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const cardWidth = track.children[0].offsetWidth;
    const idx = Math.round(track.scrollLeft / (cardWidth + 24));
    setActiveIdx(Math.max(0, Math.min(idx, members.length - 1)));
  };

  const canGoPrev = activeIdx > 0;
  const canGoNext = activeIdx + 3 < members.length;

  return (
    <div className="about-page">
      {/* Page Banner */}
      <div className="page-banner">
        <div className="container">
          <h1>À Propos de Notre Église</h1>
          <p>Fondée sur la Grâce, Unie dans l'Amour</p>
        </div>
      </div>

      {/* Our Story */}
      <section className="section">
        <div className="container">
          <div className="about-story">
            <div>
              <img
                src="https://picsum.photos/seed/church-congregation/780/440"
                alt="Congrégation de la Première Église Chrétienne De La Chambre Haute"
                className="about-church-photo"
              />
              <h2 className="section-title">Notre Histoire</h2>
              <div className="gold-bar" />
              <p>
                Fondée par le Pasteur Denel Jerome, la Première Église Chrétienne De La Chambre Haute est née
                d'une vision simple mais puissante : apporter la Bonne Nouvelle à chaque quartier
                de Saint-Marc et au-delà.
              </p>
              <p>
                Au fil des années, notre congrégation a traversé des moments difficiles — tremblements
                de terre, instabilité sociale, pertes personnelles — mais la foi collective de nos
                membres nous a permis de rester debout et de continuer à servir.
              </p>
              <p>
                Aujourd'hui, nous sommes une famille de plus de 500 membres actifs, avec 6 ministères
                dynamiques, une école biblique et un centre d'aide sociale qui touche des centaines de
                familles chaque mois.
              </p>
            </div>
            <div className="about-values">
              {[
                { icon: '📖', label: 'Parole', desc: 'Ancrés dans la vérité biblique' },
                { icon: '🙏', label: 'Prière', desc: 'Une vie de communion avec Dieu' },
                { icon: '❤️', label: 'Amour', desc: 'Servir les uns et les autres' },
                { icon: '🌍', label: 'Mission', desc: 'Atteindre chaque âme' },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="value-card card">
                  <span className="value-card__icon">{icon}</span>
                  <h4>{label}</h4>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section mission-section">
        <div className="container mv-grid">
          <div className="mv-card">
            <h2>Notre Mission</h2>
            <div className="gold-bar" />
            <p>
              Proclamer l'Évangile de Jésus-Christ, faire des disciples dans toutes les nations,
              et servir la communauté avec amour, compassion et justice.
            </p>
          </div>
          <div className="mv-card">
            <h2>Notre Vision</h2>
            <div className="gold-bar" />
            <p>
              Être une église transformatrice qui élève les individus, renforce les familles et
              renouvelle les communautés à travers la puissance de l'Évangile en Haïti et dans le monde.
            </p>
          </div>
        </div>
      </section>

      {/* Core Beliefs */}
      <section className="section">
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center' }}>Ce que nous croyons</h2>
          <div className="gold-bar" style={{ margin: '12px auto 48px' }} />
          <div className="beliefs-grid">
            {BELIEFS.map(({ title, text }) => (
              <div key={title} className="belief-card card">
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section leadership-section">
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center' }}>Notre Équipe de Direction</h2>
          <div className="gold-bar" style={{ margin: '12px auto 48px' }} />
          <div className="pastors-carousel">
            <button
              className="carousel-arrow carousel-arrow--prev"
              onClick={() => scrollToCard(Math.max(0, activeIdx - 3))}
              disabled={!canGoPrev}
              aria-label="Précédent"
            >
              &#8249;
            </button>
            <div className="pastors-track" ref={trackRef} onScroll={handleScroll}>
              {members.map(({ id, name, role, bio, photo_url }) => (
                <div key={id} className="pastor-card card">
                  {photo_url ? (
                    <img src={photo_url} alt={name} className="pastor-photo" />
                  ) : (
                    <div className="pastor-avatar">
                      {name.split(' ').slice(-2).map((n) => n[0]).join('')}
                    </div>
                  )}
                  <h3>{name}</h3>
                  <span className="badge badge-gold">{role}</span>
                  <p>{bio}</p>
                </div>
              ))}
            </div>
            <button
              className="carousel-arrow carousel-arrow--next"
              onClick={() => scrollToCard(Math.min(members.length - 1, activeIdx + 3))}
              disabled={!canGoNext}
              aria-label="Suivant"
            >
              &#8250;
            </button>
          </div>
          {members.length > 1 && (
            <div className="carousel-dots">
              {members.map((_, i) => (
                <button
                  key={i}
                  className={`carousel-dot${i === activeIdx ? ' carousel-dot--active' : ''}`}
                  onClick={() => scrollToCard(i)}
                  aria-label={`Membre ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
